import { useState, useEffect, useRef } from "react";
import logo from './logo.png';
import { ArrowUpRight, Check } from "lucide-react";

function useInView(threshold = 0.1) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setInView(true); }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

function FadeIn({ children, delay = 0 }) {
  const [ref, inView] = useInView();
  return (
    <div ref={ref} style={{
      opacity: inView ? 1 : 0,
      transform: inView ? "translateY(0)" : "translateY(28px)",
      transition: `opacity 0.7s ease ${delay}s, transform 0.7s ease ${delay}s`
    }}>{children}</div>
  );
}

function DashboardMockup() {
  return (
    <div style={{ background: "#111", borderRadius: 16, overflow: "hidden", border: "1px solid rgba(255,255,255,0.08)", boxShadow: "0 40px 120px rgba(0,0,0,0.6)" }}>
      {/* Browser bar */}
      <div style={{ padding: "10px 16px", background: "#1a1a1a", borderBottom: "1px solid rgba(255,255,255,0.06)", display: "flex", alignItems: "center", gap: 8 }}>
        <div style={{ width: 10, height: 10, borderRadius: "50%", background: "#ff5f57" }} />
        <div style={{ width: 10, height: 10, borderRadius: "50%", background: "#febc2e" }} />
        <div style={{ width: 10, height: 10, borderRadius: "50%", background: "#28c840" }} />
        <div style={{ flex: 1, margin: "0 12px", background: "rgba(255,255,255,0.06)", borderRadius: 5, padding: "4px 12px", fontSize: 10, color: "rgba(255,255,255,0.3)", fontWeight: 500 }}>binosai.com/login</div>
      </div>

      <div style={{ display: "flex", height: 480 }}>
        {/* Sidebar */}
        <div style={{ width: 180, background: "#0d0d0d", padding: "20px 12px", display: "flex", flexDirection: "column", gap: 3, flexShrink: 0, borderRight: "1px solid rgba(255,255,255,0.05)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "0 8px 18px" }}>
            <img src={logo} style={{ width: 22, height: 22 }} alt="Binos" />
            <span style={{ fontWeight: 900, fontSize: 13, color: "#fff", letterSpacing: "-0.3px" }}>Binos</span>
          </div>
          {[
            { label: "Fund Health", active: true },
            { label: "Pitch Analyser", active: false },
            { label: "Thesis Matcher", active: false },
            { label: "VC Calculator", active: false },
            { label: "Market Research", active: false },
            { label: "News Feed", active: false },
          ].map((item, i) => (
            <div key={i} style={{ padding: "8px 10px", borderRadius: 7, fontSize: 11, fontWeight: 600, color: item.active ? "#fff" : "rgba(255,255,255,0.35)", background: item.active ? "rgba(220,38,38,0.25)" : "transparent", borderLeft: item.active ? "2px solid #dc2626" : "2px solid transparent" }}>{item.label}</div>
          ))}
        </div>

        {/* Main */}
        <div style={{ flex: 1, padding: "20px 20px", background: "#f8f8f8", overflowY: "hidden" }}>
          {/* Header */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <div>
              <p style={{ fontSize: 16, fontWeight: 900, margin: "0 0 2px", color: "#0f0f0f", letterSpacing: "-0.5px" }}>Fund Health</p>
              <p style={{ fontSize: 10, color: "#999", margin: 0, fontWeight: 500 }}>3 portfolio companies · Live</p>
            </div>
            <div style={{ padding: "7px 14px", background: "#dc2626", borderRadius: 7, fontSize: 10, fontWeight: 700, color: "#fff" }}>+ Add Company</div>
          </div>

          {/* Metric cards */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8, marginBottom: 14 }}>
            {[
              { l: "Portfolio", v: "3" },
              { l: "Avg Runway", v: "14m" },
              { l: "Healthy", v: "2" },
              { l: "At Risk", v: "1" },
            ].map((m, i) => (
              <div key={i} style={{ background: "#fff", border: "1px solid #efefef", borderRadius: 10, padding: "12px 14px" }}>
                <p style={{ fontSize: 9, color: "#999", fontWeight: 600, margin: "0 0 4px", textTransform: "uppercase" }}>{m.l}</p>
                <p style={{ fontSize: 20, fontWeight: 900, color: "#0f0f0f", margin: 0, letterSpacing: "-0.5px" }}>{m.v}</p>
              </div>
            ))}
          </div>

          {/* Company cards */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10, marginBottom: 14 }}>
            {[
              { name: "NexaFlow", sector: "B2B SaaS", stage: "Series A", health: "Healthy", runway: "18m", color: "#16a34a", arr: "₹1.2Cr" },
              { name: "MediGen", sector: "HealthTech", stage: "Seed", health: "Watch", runway: "9m", color: "#d97706", arr: "₹40L" },
              { name: "EcoGrid", sector: "CleanTech", stage: "Pre-Seed", health: "At Risk", runway: "4m", color: "#dc2626", arr: "₹12L" },
            ].map((c, i) => (
              <div key={i} style={{ background: "#fff", border: `1px solid ${c.color}25`, borderRadius: 10, padding: "14px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                  <div style={{ width: 28, height: 28, background: c.color + "15", borderRadius: 7, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontWeight: 900, color: c.color }}>{c.name.slice(0, 2)}</div>
                  <span style={{ fontSize: 8, fontWeight: 800, color: c.color, background: c.color + "12", padding: "2px 7px", borderRadius: 100 }}>{c.health}</span>
                </div>
                <p style={{ fontSize: 11, fontWeight: 800, color: "#0f0f0f", margin: "0 0 1px", letterSpacing: "-0.2px" }}>{c.name}</p>
                <p style={{ fontSize: 9, color: "#999", margin: "0 0 8px", fontWeight: 500 }}>{c.sector} · {c.stage}</p>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontSize: 9, color: "#bbb", fontWeight: 500 }}>Runway</span>
                  <span style={{ fontSize: 9, fontWeight: 800, color: c.color }}>{c.runway}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 3 }}>
                  <span style={{ fontSize: 9, color: "#bbb", fontWeight: 500 }}>ARR</span>
                  <span style={{ fontSize: 9, fontWeight: 800, color: "#555" }}>{c.arr}</span>
                </div>
              </div>
            ))}
          </div>

          {/* Pitch analyser preview */}
          <div style={{ background: "#fff", border: "1px solid #efefef", borderRadius: 10, padding: "12px 14px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <p style={{ fontSize: 10, fontWeight: 800, color: "#0f0f0f", margin: 0 }}>Recent Pitch Analyses</p>
              <span style={{ fontSize: 9, color: "#dc2626", fontWeight: 700 }}>View all →</span>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
              {[
                { name: "NexaFlow_Deck.pdf", match: "94% · Strong Match", color: "#16a34a" },
                { name: "MediGen_Series_A.pdf", match: "71% · Partial Match", color: "#d97706" },
                { name: "EcoGrid_Pitch.pdf", match: "38% · Not a Fit", color: "#dc2626" },
              ].map((d, i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 8px", background: "#fafafa", borderRadius: 6 }}>
                  <span style={{ fontSize: 9, fontWeight: 600, color: "#555" }}>{d.name}</span>
                  <span style={{ fontSize: 9, fontWeight: 800, color: d.color }}>{d.match}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function LandingPage({ setView }) {
  const [scrolled, setScrolled] = useState(false);
  const [heroVisible, setHeroVisible] = useState(false);

  useEffect(() => {
    setTimeout(() => setHeroVisible(true), 80);
    const handler = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", handler);
    return () => window.removeEventListener("scroll", handler);
  }, []);

  const S = (delay) => ({
    opacity: heroVisible ? 1 : 0,
    transform: heroVisible ? "translateY(0)" : "translateY(20px)",
    transition: `opacity 0.8s ease ${delay}s, transform 0.8s ease ${delay}s`
  });

  return (
    <div style={{ fontFamily: "'DM Sans', system-ui, sans-serif", background: "#0a0a0a", color: "#fff", overflowX: "hidden" }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:ital,wght@0,400;0,500;0,700;0,900;1,400&family=DM+Serif+Display:ital@0;1&display=swap" rel="stylesheet" />

      {/* NAV */}
      <nav style={{
        position: "fixed", top: 0, left: 0, right: 0, zIndex: 100,
        height: 60, display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "0 48px",
        background: scrolled ? "rgba(10,10,10,0.95)" : "transparent",
        backdropFilter: scrolled ? "blur(20px)" : "none",
        borderBottom: scrolled ? "1px solid rgba(255,255,255,0.06)" : "none",
        transition: "all 0.35s ease"
      }}>
        <a href="/" style={{ display: "flex", alignItems: "center", gap: 10, textDecoration: "none" }}>
          <img src={logo} style={{ width: 32, height: 32 }} alt="Binos" />
          <span style={{ fontWeight: 900, fontSize: 18, color: "#fff", letterSpacing: "-0.5px" }}>Binos</span>
        </a>
        <div style={{ display: "flex", alignItems: "center", gap: 32 }}>
          {["Product"].map(l => (
            <a key={l} href={`/${l.toLowerCase()}`} style={{ fontSize: 13, fontWeight: 500, color: "rgba(255,255,255,0.5)", textDecoration: "none", transition: "color 0.2s" }}
              onMouseEnter={e => e.target.style.color = "#fff"}
              onMouseLeave={e => e.target.style.color = "rgba(255,255,255,0.5)"}>{l}</a>
          ))}
          <button onClick={() => setView('auth')} style={{ fontSize: 13, fontWeight: 600, color: "rgba(255,255,255,0.6)", background: "none", border: "none", cursor: "pointer", padding: 0, fontFamily: "inherit" }}>Log in</button>
          <button onClick={() => window.open("https://cal.com/anas-khakhu-d2bwbc", "_blank")} style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 18px", background: "#dc2626", color: "#fff", borderRadius: 8, fontSize: 13, fontWeight: 700, border: "none", cursor: "pointer", fontFamily: "inherit", transition: "opacity 0.2s" }}
            onMouseEnter={e => e.currentTarget.style.opacity = "0.85"}
            onMouseLeave={e => e.currentTarget.style.opacity = "1"}>
            Book a Demo <ArrowUpRight style={{ width: 13, height: 13 }} />
          </button>
        </div>
      </nav>

      {/* HERO — dark, full screen */}
      <section style={{ minHeight: "100vh", display: "flex", flexDirection: "column", justifyContent: "center", padding: "100px 48px 60px", position: "relative", overflow: "hidden" }}>
        {/* Background grid */}
        <div style={{ position: "absolute", inset: 0, backgroundImage: "linear-gradient(rgba(255,255,255,0.02) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.02) 1px, transparent 1px)", backgroundSize: "48px 48px", pointerEvents: "none" }} />
        {/* Red glow top center */}
        <div style={{ position: "absolute", top: -100, left: "50%", transform: "translateX(-50%)", width: 900, height: 500, background: "radial-gradient(ellipse, rgba(220,38,38,0.12) 0%, transparent 65%)", pointerEvents: "none" }} />

        <div style={{ maxWidth: 1100, margin: "0 auto", width: "100%", position: "relative", zIndex: 1 }}>

          {/* Headline */}
          <div style={S(0.1)}>
            <h1 style={{ fontFamily: "'DM Serif Display', Georgia, serif", fontSize: "clamp(52px, 8vw, 96px)", fontWeight: 400, color: "#fff", lineHeight: 1.0, letterSpacing: "-3px", margin: "0 0 20px", maxWidth: 800 }}>
              The first operating system built for <span style={{ color: "#dc2626", fontStyle: "italic" }}>venture capital.</span>
            </h1>
          </div>

          {/* Tagline */}
          <div style={S(0.2)}>
            <p style={{ fontSize: 18, color: "rgba(255,255,255,0.45)", fontWeight: 400, lineHeight: 1.75, maxWidth: 560, margin: "0 0 36px" }}>
              AI-Powered Venture Intelligence for the Entire Deal Lifecycle.
            </p>
          </div>

          {/* CTAs */}
          <div style={{ ...S(0.3), display: "flex", gap: 12, alignItems: "center", marginBottom: 64 }}>
            <button onClick={() => window.open("https://cal.com/anas-khakhu-d2bwbc", "_blank")}
              style={{ padding: "13px 28px", background: "#dc2626", color: "#fff", borderRadius: 10, fontSize: 14, fontWeight: 700, border: "none", cursor: "pointer", fontFamily: "inherit", boxShadow: "0 4px 24px rgba(220,38,38,0.35)" }}>
              Book a Demo ↗
            </button>
          </div>

          {/* Dashboard screenshot — LARGE */}
          <div style={S(0.4)}>
            <DashboardMockup />
          </div>

          {/* Stats strip */}
          <div style={{ ...S(0.5), display: "flex", gap: 0, marginTop: 48, borderTop: "1px solid rgba(255,255,255,0.06)", paddingTop: 40 }}>
            {[
              { n: "250+", l: "Active VC funds in India" },
              { n: "60s", l: "To analyse any pitch deck" },
              { n: "70%", l: "Time saved on screening" },
              { n: "₹180Cr+", l: "Market opportunity" },
            ].map((s, i) => (
              <div key={i} style={{ flex: 1, textAlign: "center", borderRight: i < 3 ? "1px solid rgba(255,255,255,0.06)" : "none", padding: "0 24px" }}>
                <p style={{ fontSize: 32, fontWeight: 900, color: "#fff", margin: "0 0 6px", letterSpacing: "-1.5px" }}>{s.n}</p>
                <p style={{ fontSize: 12, color: "rgba(255,255,255,0.35)", fontWeight: 500, margin: 0 }}>{s.l}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES — light */}
      <section style={{ padding: "100px 48px", background: "#fff" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <FadeIn>
            <p style={{ fontSize: 11, fontWeight: 700, color: "#dc2626", letterSpacing: "0.2em", textTransform: "uppercase", marginBottom: 16, textAlign: "center" }}>Find signal. Build conviction. Deploy faster.</p>
            <h2 style={{ fontFamily: "'DM Serif Display', Georgia, serif", fontSize: "clamp(36px, 4vw, 52px)", fontWeight: 400, letterSpacing: "-1.5px", margin: "0 auto 64px", color: "#0f0f0f", textAlign: "center", maxWidth: 560, lineHeight: 1.1 }}>
              Built for the way<br />serious funds work.
            </h2>
          </FadeIn>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 20 }}>
            {[
              { tag: "Pitch Intelligence", title: "Read any pitch deck in 60 seconds.", body: "Canva, image-heavy PDFs, scanned documents — Binos reads them all. AI extracts 9 investment signals automatically.", bullets: ["Works on any PDF format", "9 investment signals extracted", "Bulk upload up to 50 decks"] },
              { tag: "Thesis Matcher", title: "Find deals that match how you think.", body: "Upload your investment thesis in plain English. Binos scores every company against your exact criteria and ranks them.", bullets: ["Plain English thesis input", "AI scores 0–100 per company", "Full reasoning for every match"] },
              { tag: "Portfolio Health", title: "Never miss a portfolio red flag.", body: "Real-time runway tracking, founder CRM, and health monitoring across every company in your portfolio.", bullets: ["Runway alerts before crisis", "Founder CRM built in", "7 tabs of company data"] },
              { tag: "VC Finance Engine", title: "Investment-grade financial analysis.", body: "Burn Multiple, Rule of 40, LTV:CAC, Magic Number — calculated instantly with a clear Invest, Watch, or Pass verdict.", bullets: ["10+ VC-specific metrics", "Custom metric bundles", "Supports INR, USD, EUR and more"] },
            ].map((f, i) => (
              <FadeIn key={i} delay={i * 0.08}>
                <div style={{ padding: "36px 36px", background: "#fff", border: "1px solid #efefef", borderRadius: 18, transition: "all 0.3s", cursor: "default" }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = "#dc2626"; e.currentTarget.style.transform = "translateY(-3px)"; e.currentTarget.style.boxShadow = "0 16px 48px rgba(0,0,0,0.07)"; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = "#efefef"; e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; }}>
                  <p style={{ fontSize: 10, fontWeight: 800, color: "#dc2626", letterSpacing: "0.2em", textTransform: "uppercase", margin: "0 0 14px" }}>{f.tag}</p>
                  <h3 style={{ fontSize: 20, fontWeight: 800, color: "#0f0f0f", margin: "0 0 12px", letterSpacing: "-0.3px", lineHeight: 1.25 }}>{f.title}</h3>
                  <p style={{ fontSize: 14, color: "#666", lineHeight: 1.75, margin: "0 0 20px" }}>{f.body}</p>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {f.bullets.map((b, j) => (
                      <div key={j} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <div style={{ width: 16, height: 16, borderRadius: "50%", background: "#dc262610", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                          <Check style={{ width: 9, height: 9, color: "#dc2626" }} />
                        </div>
                        <p style={{ fontSize: 13, fontWeight: 500, color: "#555", margin: 0 }}>{b}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>


      {/* CTA */}
      <section style={{ padding: "100px 48px", background: "#0a0a0a", borderTop: "1px solid rgba(255,255,255,0.04)", textAlign: "center", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", width: 700, height: 400, background: "radial-gradient(ellipse, rgba(220,38,38,0.08) 0%, transparent 70%)", pointerEvents: "none" }} />
        <FadeIn>
          <div style={{ position: "relative", zIndex: 1 }}>
            <h2 style={{ fontFamily: "'DM Serif Display', Georgia, serif", fontSize: "clamp(40px, 6vw, 68px)", fontWeight: 400, color: "#fff", letterSpacing: "-2px", lineHeight: 1.1, margin: "0 0 16px" }}>
              Your entire fund.<br />
              <span style={{ fontStyle: "italic", color: "#dc2626" }}>One screen.</span>
            </h2>
            <p style={{ fontSize: 16, color: "rgba(255,255,255,0.35)", margin: "0 auto 40px", maxWidth: 400, lineHeight: 1.7 }}>
              Join the first Indian VC firms on Binos.
            </p>
            <button onClick={() => window.open("https://cal.com/anas-khakhu-d2bwbc", "_blank")} style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "14px 32px", background: "#dc2626", color: "#fff", borderRadius: 10, fontSize: 15, fontWeight: 700, border: "none", cursor: "pointer", fontFamily: "inherit", boxShadow: "0 4px 24px rgba(220,38,38,0.3)", transition: "all 0.2s" }}
              onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 8px 32px rgba(220,38,38,0.4)"; }}
              onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 4px 24px rgba(220,38,38,0.3)"; }}>
              Book a Demo <ArrowUpRight style={{ width: 16, height: 16 }} />
            </button>
          </div>
        </FadeIn>
      </section>

      {/* FOOTER */}
      <footer style={{ background: "#060606", padding: "36px 48px", borderTop: "1px solid rgba(255,255,255,0.04)" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <img src={logo} style={{ width: 28, height: 28 }} alt="Binos" />
            <span style={{ fontWeight: 900, fontSize: 15, color: "#fff", letterSpacing: "-0.5px" }}>Binos</span>
            <span style={{ fontSize: 11, color: "rgba(255,255,255,0.2)", marginLeft: 8 }}>The VC Operating System</span>
          </div>
          <p style={{ fontSize: 11, color: "rgba(255,255,255,0.2)", margin: 0 }}>© 2025 Binos. Built in Mumbai.</p>
          <div style={{ display: "flex", gap: 24 }}>
            {["Privacy", "Terms"].map(l => (
              <a key={l}
                href={`/${l.toLowerCase()}`}
                style={{ fontSize: 11, color: "rgba(255,255,255,0.25)", textDecoration: "none", fontWeight: 500, transition: "color 0.2s", cursor: "pointer" }}
                onMouseEnter={e => e.target.style.color = "rgba(255,255,255,0.7)"}
                onMouseLeave={e => e.target.style.color = "rgba(255,255,255,0.25)"}>{l}</a>
            ))}
          </div>
        </div>
      </footer>
    </div>
  );
}
